import { useMemo, useState } from "react";
import {
  Award,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Target,
  Trash2,
} from "lucide-react";
import { formatBRL, formatDateUTC } from "@/lib/format";
import type { SettlementSummary } from "@/lib/leaderboard";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import type { Tournament } from "@/types";

// Porta 1:1 de reference/expo-frontend/components/MyBetsList.tsx.
// Adaptações web: nomes vêm do snapshot playerNames (sem roster local, sem
// coluna #worldRank) e o resultado por aposta usa winnerPayouts.betId — o
// fallback por top3Ids do torneio não existe (a API web não expõe top3Ids).
//
// Divergente do original (pedido do produto): apostas de torneio JÁ ENCERRADO
// nascem COLAPSADAS — só cabeçalho (torneio, data, valor, prêmio, badge) e um
// chevron que revela os picks. Sem isso, um histórico de 3 linhas por aposta
// empurra tudo abaixo da dobra e esconde o palpite do torneio em andamento,
// que é o que interessa na hora.

export type MyBetsListBet = {
  id: string;
  tournamentId: string;
  playerIds: string[];
  playerNames: string[];
  amount: number;
  createdAt: string;
};

export type LiveLeaderboardEntry = {
  playerId: string;
  position: number | null;
  totalScore?: number | null;
};

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return "";
  if (score === 0) return "E";
  return score > 0 ? `+${score}` : `${score}`;
}

export function MyBetsList({
  bets,
  tournaments,
  settlements,
  limit,
  onSeeAll,
  liveLeaderboard,
  liveLeaderboardTournamentId,
  officialTop3Ids,
  onDelete,
  deletingBetId,
}: {
  bets: MyBetsListBet[];
  tournaments: Tournament[];
  settlements: SettlementSummary[];
  limit?: number;
  onSeeAll?: (total: number) => void;
  liveLeaderboard?: LiveLeaderboardEntry[];
  // O leaderboard é de UM torneio (o em destaque); sem este id, uma aposta
  // em outro torneio "active" (semanas sobrepostas) mostraria posições do
  // evento errado.
  liveLeaderboardTournamentId?: string;
  // IDs do top-3 resolvidos (do leaderboard ao vivo). Destaque âmbar por
  // pertencimento exato, não por position <= 3 (que super-destaca empates).
  officialTop3Ids?: string[];
  onDelete?: (betId: string, tournamentId: string) => void;
  deletingBetId?: string | null;
}) {
  const [showAll, setShowAll] = useState(false);
  // Apostas de torneio encerrado que o usuário abriu manualmente (por betId).
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleExpanded = (betId: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(betId)) next.delete(betId);
      else next.add(betId);
      return next;
    });

  // Lista dos 3 picks (posição/score ao vivo, CUT, destaque top-3). Extraída
  // porque é renderizada por dois caminhos: card aberto e card encerrado que
  // o usuário expandiu.
  const renderPicks = (
    bet: MyBetsListBet,
    showLive: boolean,
    settlementCutSet: Set<string> | null,
  ) => (
    <div className="mt-2.5 flex flex-col gap-[5px]">
      {bet.playerIds.map((pid, i) => {
        const live = showLive ? livePositionMap.get(pid) : undefined;
        // O feed omite quem perdeu o corte — pick ausente com o leaderboard
        // carregado é CUT; posição null idem. Torneios encerrados sem
        // leaderboard usam o CUT set da liquidação.
        const isCut =
          (showLive && (!live || live.position === null)) ||
          (!showLive && !!settlementCutSet && settlementCutSet.has(pid));
        const scoreStr =
          live && live.position !== null ? formatScore(live.totalScore) : "";
        const liveMeta =
          live && live.position !== null
            ? scoreStr
              ? `T${live.position} · ${scoreStr}`
              : `T${live.position}`
            : isCut
              ? "CUT"
              : null;
        const isTop3 =
          officialTop3Ids && officialTop3Ids.length > 0
            ? officialTop3Ids.includes(pid)
            : !!live && live.position !== null && live.position <= 3;
        return (
          <div key={pid} className="flex items-center gap-2">
            <p
              className={`min-w-0 flex-1 truncate text-[13px] font-medium ${
                isTop3 ? "text-accent" : isCut ? "text-muted-foreground" : ""
              }`}
            >
              {bet.playerNames[i]?.trim() || pid}
            </p>
            {liveMeta && (
              <p
                className={`shrink-0 text-xs ${
                  isCut ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {liveMeta}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  const livePositionMap = useMemo(() => {
    const map = new Map<
      string,
      { position: number | null; totalScore?: number | null }
    >();
    for (const entry of liveLeaderboard ?? []) {
      map.set(entry.playerId, {
        position: entry.position,
        totalScore: entry.totalScore,
      });
    }
    return map;
  }, [liveLeaderboard]);

  if (bets.length === 0) {
    return (
      <Card className="flex items-center gap-2.5 rounded-(--radius-btn) p-4">
        <Target size={18} className="shrink-0 text-muted-foreground" />
        <p className="flex-1 text-[13px] text-muted-foreground">
          Você ainda não fez nenhum palpite.
        </p>
      </Card>
    );
  }

  const sorted = [...bets].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const capped = typeof limit === "number" && !showAll;
  const visible = capped ? sorted.slice(0, limit) : sorted;

  return (
    <div className="flex flex-col gap-2.5">
      {visible.map((bet) => {
        const t = tournaments.find((x) => x.id === bet.tournamentId);
        const settlement = settlements.find(
          (s) => s.tournamentId === bet.tournamentId,
        );
        // Gate pelo deadline, não pelo status: status é derivado das datas e
        // fica "settled" mesmo com apostas reabertas pelo admin.
        const isOpen = !!t && new Date(t.deadline).getTime() > Date.now();
        const showLive =
          !!t &&
          (t.status === "active" || t.status === "settled") &&
          t.id === liveLeaderboardTournamentId &&
          livePositionMap.size > 0;
        // CUTs persistidos na liquidação — disponíveis para qualquer torneio
        // encerrado, mesmo sem leaderboard ao vivo nesta sessão.
        const settlementCutSet =
          settlement?.cutPlayerIds && settlement.cutPlayerIds.length > 0
            ? new Set(settlement.cutPlayerIds)
            : null;
        // Pick ausente do feed carregado (ou posição null) = CUT/WD/DQ —
        // derrota definitiva antes mesmo da liquidação.
        const anyPickCut =
          (showLive &&
            bet.playerIds.some((pid) => {
              const live = livePositionMap.get(pid);
              return !live || live.position === null;
            })) ||
          (!showLive &&
            !!settlementCutSet &&
            bet.playerIds.some((pid) => settlementCutSet.has(pid)));
        const decided = !!settlement || anyPickCut;
        const isWaitingForResult = t?.status === "settled" && !decided;
        let won: boolean | null = null;
        let prize = 0;
        if (decided) {
          if (anyPickCut && !settlement) {
            won = false;
          } else if (settlement?.winnerPayouts) {
            const payout = settlement.winnerPayouts.find(
              (p) => p.betId === bet.id,
            );
            won = !!payout;
            prize = payout?.prize ?? 0;
          }
          // Liquidações antigas sem winnerPayouts não permitem determinar o
          // resultado POR APOSTA; mantém "Aguardando resultado".
        }
        const badge =
          won === true
            ? {
                className: "bg-success text-success-foreground",
                icon: <Award size={11} />,
                label: "ACERTOU",
              }
            : won === false
              ? { className: "", icon: null, label: "😢" }
              : isWaitingForResult
                ? {
                    className: "bg-muted text-muted-foreground",
                    icon: <Clock size={11} />,
                    label: "AGUARDANDO RESULTADO",
                  }
                : {
                    className: "bg-success text-success-foreground",
                    icon: <Check size={11} />,
                    label: "CONFIRMADA",
                  };
        // "Encerrado" = confirmação oficial (finished_at) ou liquidação gravada.
        // NÃO usar `decided`: um pick cortado no meio do torneio decide a
        // aposta, mas o evento segue em andamento e o usuário quer acompanhar.
        const isFinished = !!t?.finishedAt || !!settlement;
        const collapsed = isFinished && !expandedIds.has(bet.id);
        const header = (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">
                {t?.name ?? "Torneio"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t ? formatDateUTC(t.startDate) : ""}
                {t ? " · " : ""}
                Aposta: {formatBRL(bet.amount)}
                {won === true && prize > 0
                  ? ` · Prêmio: ${formatBRL(prize)}`
                  : ""}
              </p>
            </div>
            <span
              className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[9px] font-bold tracking-[0.4px] ${badge.className} ${
                badge.label === "😢" ? "text-[22px] leading-none" : ""
              }`}
            >
              {badge.label !== "😢" && badge.icon}
              {badge.label}
            </span>
          </>
        );

        if (isFinished) {
          return (
            <Card key={bet.id} className="rounded-(--radius-btn) p-3.5">
              {/* Torneio encerrado não tem exclusão (isOpen=false), então o
                  cabeçalho inteiro pode ser o botão — sem botão aninhado. */}
              <button
                type="button"
                onClick={() => toggleExpanded(bet.id)}
                aria-expanded={!collapsed}
                aria-label={
                  collapsed
                    ? `Ver palpites de ${t?.name ?? "torneio"}`
                    : `Ocultar palpites de ${t?.name ?? "torneio"}`
                }
                className="flex w-full items-start gap-2.5 text-left"
              >
                {header}
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-muted-foreground transition-transform ${
                    collapsed ? "" : "rotate-180"
                  }`}
                />
              </button>
              {!collapsed && renderPicks(bet, showLive, settlementCutSet)}
            </Card>
          );
        }

        return (
          <Card key={bet.id} className="rounded-(--radius-btn) p-3.5">
            <div className="flex items-start gap-2.5">
              {header}
              {isOpen && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Excluir palpite? O valor apostado será devolvido ao seu saldo.",
                      )
                    ) {
                      onDelete(bet.id, bet.tournamentId);
                    }
                  }}
                  disabled={deletingBetId === bet.id}
                  aria-label="Excluir palpite"
                  className="shrink-0 p-0.5 text-muted-foreground opacity-60 transition-opacity hover:opacity-100 disabled:opacity-40"
                >
                  {deletingBetId === bet.id ? (
                    <Spinner size={15} />
                  ) : (
                    <Trash2 size={15} />
                  )}
                </button>
              )}
            </div>
            {renderPicks(bet, showLive, settlementCutSet)}
          </Card>
        );
      })}
      {typeof limit === "number" && sorted.length > limit && (
        <button
          type="button"
          onClick={() => {
            if (onSeeAll) onSeeAll(sorted.length);
            else setShowAll((v) => !v);
          }}
          className="flex items-center justify-center gap-1.5 rounded-[10px] bg-secondary py-2.5 text-[13px] font-semibold transition-opacity hover:opacity-80"
        >
          {onSeeAll
            ? `Ver todos (${sorted.length})`
            : showAll
              ? "Mostrar menos"
              : `Ver todos (${sorted.length})`}
          <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}
